import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, Mic2, Power } from 'lucide-react';
import { toast } from 'sonner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { RequestsPanel } from '../../components/karaoke/RequestsPanel';
import { PlaylistPanel } from '../../components/karaoke/PlaylistPanel';
import { TheatrePanel } from '../../components/karaoke/TheatrePanel';
import { QrPanel, StaffRequestForm, TeamPanel } from '../../components/karaoke/KaraokeExtras';
import { errMsg, kApi, useKaraokeState } from '../../lib/karaokeApi';

const ROLE_LABEL = { dj: 'DJ', host: 'Host', operator: 'Operator' };

const NowNext = ({ data }) => {
  const playing = data.playlist.find((p) => p.status === 'playing');
  const next = data.playlist.find((p) => p.id === data.next_id);
  const card = (label, p, cls) => (
    <div className={`rounded-2xl p-4 text-white ${cls}`}>
      <p className="text-xs uppercase tracking-widest text-white/70 font-semibold">{label}</p>
      {p ? <><p className="text-xl font-black break-words">{p.guest_name}</p><p className="text-sm text-white/90 break-words">{p.title || 'Untitled video'}</p></> : <p className="text-white/70 mt-1">—</p>}
    </div>
  );
  return (
    <div className="grid sm:grid-cols-2 gap-3" data-testid="karaoke-now-next">
      {card('Now singing', playing, 'bg-gradient-to-br from-fuchsia-600 to-purple-800')}
      {card('Next singer', next, 'bg-gradient-to-br from-slate-800 to-slate-950')}
    </div>
  );
};

export default function KaraokeDashboard() {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [denied, setDenied] = useState(null);
  const [data, refresh] = useKaraokeState(2000);
  const [code, setCode] = useState('');

  useEffect(() => {
    kApi.me().then(setMe).catch((e) => setDenied(errMsg(e, 'No access')));
    kApi.settings().then((s) => setCode(s.public_code)).catch(() => {});
  }, []);

  if (denied) return <div className="min-h-screen flex flex-col items-center justify-center gap-3 p-6 text-center"><Mic2 className="w-10 h-10 text-slate-400" /><p className="font-semibold">{denied}</p><p className="text-sm text-slate-500">Ask your Chief Animator to add you to the IC.Karaoke team.</p></div>;
  if (!me || !data) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>;

  const ctl = me.can_control;
  const session = data.active ? data.session : null;
  const toggleSession = async () => {
    try {
      if (session) { if (!window.confirm('End this Karaoke Night? History is kept.')) return; await kApi.endSession(session.id); toast.success('Session ended'); } else { await kApi.startSession(); toast.success('Karaoke Night started'); }
      refresh();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="karaoke-dashboard">
      <header className="bg-gradient-to-r from-[#1a0530] via-[#3b0764] to-[#0c1a3a] text-white">
        <div className="max-w-6xl mx-auto px-4 py-5 flex flex-wrap items-center gap-4">
          <button type="button" onClick={() => navigate(-1)} className="h-10 w-10 rounded-xl bg-white/10 inline-flex items-center justify-center" aria-label="Back"><ArrowLeft className="w-5 h-5" /></button>
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl font-black tracking-tight flex items-center gap-2"><Mic2 className="w-6 h-6 text-fuchsia-300" /> IC.Karaoke</h1>
            <p className="text-sm text-white/70">{me.hotel_name} · {me.is_manager ? 'Manager' : ROLE_LABEL[me.karaoke_role]}{session ? ` · Live since ${new Date(session.started_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : ' · No active session'}</p>
          </div>
          {ctl && (
            <button type="button" onClick={toggleSession} className={`h-11 px-5 rounded-xl font-bold inline-flex items-center gap-2 ${session ? 'bg-rose-500' : 'bg-emerald-500'}`} data-testid="karaoke-session-toggle">
              <Power className="w-4 h-4" /> {session ? 'End Karaoke Night' : 'Start Karaoke Night'}
            </button>
          )}
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6 space-y-6">
        {session && <NowNext data={data} />}
        <Tabs defaultValue={session ? 'requests' : (ctl ? 'theatre' : 'qr')}>
          <TabsList className="flex flex-wrap h-auto gap-1">
            <TabsTrigger value="requests" data-testid="karaoke-tab-requests">Guest Requests{session ? ` (${data.requests.length})` : ''}</TabsTrigger>
            <TabsTrigger value="playlist" data-testid="karaoke-tab-playlist">Ready Playlist</TabsTrigger>
            <TabsTrigger value="theatre" data-testid="karaoke-tab-theatre">Theatre</TabsTrigger>
            <TabsTrigger value="qr" data-testid="karaoke-tab-qr">QR Code</TabsTrigger>
            {me.is_manager && <TabsTrigger value="team" data-testid="karaoke-tab-team">Team</TabsTrigger>}
          </TabsList>
          <TabsContent value="requests" className="space-y-4 pt-4">
            {!session ? <p className="text-sm text-slate-500">No active Karaoke session.</p> : <>
              {me.can_request && <StaffRequestForm onDone={refresh} />}
              <RequestsPanel requests={data.requests} canControl={ctl} onChanged={refresh} />
            </>}
          </TabsContent>
          <TabsContent value="playlist" className="pt-4">
            {!session ? <p className="text-sm text-slate-500">No active Karaoke session.</p> : <PlaylistPanel playlist={data.playlist} nextId={data.next_id} canControl={ctl} onChanged={refresh} />}
          </TabsContent>
          <TabsContent value="theatre" className="pt-4"><TheatrePanel display={session?.display} canControl={ctl} onChanged={refresh} /></TabsContent>
          <TabsContent value="qr" className="pt-4">{code && <QrPanel code={code} />}</TabsContent>
          {me.is_manager && <TabsContent value="team" className="pt-4"><TeamPanel /></TabsContent>}
        </Tabs>
      </main>
    </div>
  );
}
